import { createStyles } from '@mantine/core';
import { useRouter } from 'next/router';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';

import { useDepositContinueHandler } from '../hooks/useDepositContinueHandler';

import DepositCompleted from './DepositCompleted';

const useStyles = createStyles((theme) => ({
  container: {
    width: 480,

    [theme.fn.smallerThan('sm')]: {
      width: '100%',
    },
  },
}));

type DepositCompletedModalProps = {
  isOpen?: boolean;
  onClose: VoidFunction;
  depositAmount: number;
  depositResponse?: IPostWalletsDepositsResponse;
  redirectTo?: string;
  contestId?: string; // If provided, just close modal (don't navigate) to allow EnterContestModal to reappear
};

function DepositCompletedModal({
  isOpen = true,
  onClose,
  depositAmount,
  depositResponse,
  redirectTo,
  contestId: contestIdFromProps,
}: DepositCompletedModalProps) {
  const { classes } = useStyles();
  const router = useRouter();
  const { contestId: contestIdFromQuery } = router.query;

  // Check for contestId in props (from DepositView), query params, or sessionStorage
  // Props take precedence as it indicates we came from EnterContestModal flow
  const contestIdFromSession =
    typeof window !== 'undefined' ? window.sessionStorage.getItem('modal_contest_id') : null;
  const finalContestId =
    contestIdFromProps || (contestIdFromQuery as string) || contestIdFromSession || undefined;

  // Use shared continue handler for navigation (only used if contestIdFromProps is not provided)
  const continueHandler = useDepositContinueHandler({
    redirectTo,
    contestId: contestIdFromProps ? undefined : finalContestId, // Don't pass contestId if it came from props (we'll just close instead)
    onClose, // Pass modal's onClose so it closes before navigation
  });

  // If contestId is provided as prop (from DepositView), it means we came from EnterContestModal
  // In this case, just close the modal to allow EnterContestModal to reappear (don't navigate)
  // Otherwise, use the shared continue handler for navigation
  const handleOnContinue: React.MouseEventHandler = (e) => {
    e.preventDefault();
    if (contestIdFromProps) {
      // Came from EnterContestModal - just close to allow it to reappear
      onClose();
    } else {
      // Use shared continue handler for navigation
      continueHandler();
    }
  };

  return (
    <Dialog
      onClose={onClose}
      isOpen={isOpen}
      type={{
        desktop: 'modal',
        mobile: 'modal-full-screen',
      }}
      className={classes.container}
    >
      <DialogBody>
        <DepositCompleted
          depositAmount={depositAmount}
          depositResponse={depositResponse}
          onClose={handleOnContinue}
          contestId={finalContestId}
          redirectTo={redirectTo}
        />
      </DialogBody>
    </Dialog>
  );
}

export default DepositCompletedModal;
